import {
  BLOCKED_HOSTNAME_SET,
  DEFAULT_RAW_FONT_FORMAT,
  FALLBACK_FONT_FAMILY,
  FONT_FORMAT_BY_EXT,
  FONT_PRECONNECT_RULES,
  GOOGLE_BODY_WEIGHTS,
  GOOGLE_FONTS_API_HOST,
  GOOGLE_FONTS_DISPLAY,
  GOOGLE_HEADING_WEIGHTS,
  GOOGLE_MERGED_WEIGHTS,
  MAX_FONT_FAMILY_NAME_LENGTH,
  RAW_FILE_EXTENSION_SET,
  SYSTEM_FONT_SET,
} from '@/constants';
import type { IStoreMetadata } from '@/data';
import type { FontRole, RawFontFile, UnresolvedFontContext } from '@/types';

import { aspectRatioToPaddingTop } from './utils';

/**
 * Builds inline CSS custom properties for the product card slice from store metadata.
 */
export function getCSSVariablesProductCard(data: IStoreMetadata): string {
  const { productCard } = data;

  if (!productCard) return '';

  const paddingTop = aspectRatioToPaddingTop(
    productCard?.productImageAspectRatio,
  );

  const mixBlendMode = productCard?.removeBgFromImage ? 'multiply' : undefined;

  const cssVars: Record<string, string | undefined | null> = {
    '--product-card-img-aspect-ratio': paddingTop,
    '--product-card-img-object-fit': productCard.productImageStyle || 'contain',
    '--product-card-img-mix-blend-mode': mixBlendMode,
  };

  return Object.entries(cssVars)
    .filter(([, value]) => typeof value === 'string')
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ');
}

/**
 * Returns whether the hostname points to a local or private network address.
 */
function isPrivateOrLocalHost(hostname: string): boolean {
  const host = hostname.toLowerCase();

  if (BLOCKED_HOSTNAME_SET.has(host)) {
    return true;
  }

  if (host.endsWith('.local')) {
    return true;
  }

  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return true;
  }

  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return true;
  }

  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return true;
  }

  return false;
}

/**
 * Returns true when the URL points to a raw font file (.woff2, .woff, .ttf, .otf).
 */
export function isRawFontFile(url: string): boolean {
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0] ?? '';
  return RAW_FILE_EXTENSION_SET.has(ext);
}

/**
 * Validates a font URL from the CMS. Only HTTPS URLs on public hosts are accepted.
 * Returns the normalized href or null when invalid.
 */
export function validateFontUrl(url: string): string | null {
  try {
    const parsed = new URL(url.trim());

    if (parsed.protocol !== 'https:') {
      return null;
    }

    if (isPrivateOrLocalHost(parsed.hostname)) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Builds a Google Fonts CSS2 stylesheet URL for the given family and weight axis.
 */
export function buildGoogleFontsCssUrl(
  familyName: string,
  weights: string,
): string {
  const family = encodeURIComponent(familyName.trim());
  return `https://fonts.googleapis.com/css2?family=${family}:wght@${weights}&display=${GOOGLE_FONTS_DISPLAY}`;
}

/** Google Fonts stylesheet URL used when font resolution falls back to Poppins. */
export const FALLBACK_GOOGLE_URL = buildGoogleFontsCssUrl(
  FALLBACK_FONT_FAMILY,
  GOOGLE_MERGED_WEIGHTS,
);

/**
 * Normalizes a Prismic font name for CSS (e.g. DM_Sans → DM Sans).
 */
export function normalizeFontFamilyName(name: string): string {
  return name.trim().replace(/_/g, ' ');
}

/**
 * Returns true when the URL is served from the Google Fonts API host.
 */
export function isGoogleFontsUrl(url: string): boolean {
  try {
    return new URL(url).hostname === GOOGLE_FONTS_API_HOST;
  } catch {
    return false;
  }
}

/**
 * Returns the default Google weight axis for heading or body when auto-building a URL.
 */
export function getGoogleWeightsForRole(role: FontRole): string {
  return role === 'heading' ? GOOGLE_HEADING_WEIGHTS : GOOGLE_BODY_WEIGHTS;
}

/**
 * Strips unsafe characters from a font-family name before injecting it into CSS.
 */
export function sanitizeFamilyName(name: string): string | null {
  const cleaned = name
    .trim()
    .replace(/[;{}'"\\<>]/g, '')
    .slice(0, MAX_FONT_FAMILY_NAME_LENGTH);

  if (!cleaned) {
    return null;
  }

  return cleaned;
}

/**
 * Returns true when the name is a generic CSS system font keyword.
 */
export function isSystemFont(name: string): boolean {
  return SYSTEM_FONT_SET.has(name.trim().toLowerCase());
}

/**
 * Returns true for legacy Google names stored with underscores (e.g. Nunito_Sans).
 */
export function isGoogleLegacyName(name: string): boolean {
  const trimmed = name.trim();
  return /_/.test(trimmed) && !/\s/.test(trimmed);
}

/**
 * Returns true when an empty URL should be resolved via auto-generated Google Fonts CSS.
 */
export function isGoogleAutoEligible(name: string): boolean {
  const trimmed = name.trim();

  if (!trimmed || isSystemFont(trimmed)) {
    return false;
  }

  return (
    isGoogleLegacyName(trimmed) ||
    /\s/.test(trimmed) ||
    /^[A-Za-z0-9]+$/.test(trimmed)
  );
}

/**
 * Merges multiple Google weight lists into a single sorted, deduplicated axis string.
 */
export function mergeGoogleWeightLists(...lists: string[]): string {
  const weights = new Set<number>();

  for (const list of lists) {
    for (const part of list.split(';')) {
      const value = parseInt(part.trim(), 10);
      if (Number.isFinite(value)) {
        weights.add(value);
      }
    }
  }

  return [...weights].sort((a, b) => a - b).join(';');
}

/**
 * Maps a font file URL extension to the format value used in @font-face rules.
 */
export function getFontFormat(url: string): string {
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0] ?? '';
  return FONT_FORMAT_BY_EXT[ext] ?? DEFAULT_RAW_FONT_FORMAT;
}

/**
 * Escapes single quotes in a font-family name for safe use inside CSS strings.
 */
export function escapeFontFamilyForCss(value: string): string {
  return value.replace(/'/g, "\\'");
}

/**
 * Logs a development-only warning when font resolution falls back to Poppins.
 */
export function warnUnresolvedFont(
  role: string,
  context: UnresolvedFontContext,
): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn('[fonts] Unresolved font, falling back to Poppins', {
    role,
    ...context,
  });
}

/**
 * Builds a single @font-face rule for a raw font file URL.
 */
export function buildFontFaceCSS(name: string, url: string): string {
  const format = getFontFormat(url);
  const safeName = escapeFontFamilyForCss(name);

  return `
@font-face {
  font-family: '${safeName}';
  src: url('${url}') format('${format}');
  font-weight: 100 900;
  font-style: normal;
  font-display: ${GOOGLE_FONTS_DISPLAY};
}`.trim();
}

/**
 * Concatenates @font-face rules for all raw font files in the resolved theme.
 */
export function buildRawFontFacesCSS(rawFiles: RawFontFile[]): string {
  return rawFiles
    .map(({ href, name }) => buildFontFaceCSS(name, href))
    .join('\n');
}

/**
 * Builds :root CSS variables for body and heading font-family.
 */
export function buildTypographyCSSVariables(
  headingFamily: string,
  bodyFamily: string,
): string {
  return `
:root {
  --font-family: "${escapeFontFamilyForCss(
    bodyFamily,
  )}", ui-sans-serif, system-ui, sans-serif;
  --heading-font-family: "${escapeFontFamilyForCss(headingFamily)}", serif;
}`.trim();
}

/**
 * Derives unique preconnect origins from resolved external stylesheet URLs.
 */
export function getPreconnectOrigins(stylesheets: string[]): string[] {
  const origins = new Set<string>();

  for (const href of stylesheets) {
    try {
      const { hostname } = new URL(href);

      for (const rule of FONT_PRECONNECT_RULES) {
        if (hostname.includes(rule.hostIncludes)) {
          origins.add(rule.origin);
        }
      }
    } catch {
      // ignore invalid URLs
    }
  }

  return [...origins];
}
